import { useState } from 'react';
import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TabHeader from '../../../../components/TabHeader';
import ConfirmDeleteDialog from '../../../../components/ConfirmDeleteDialog';
import type { SettingsMachineTemplateRow } from '../../types';
import MachineTemplateRow from './MachineTemplateRow';
import MachineTemplateModal from './MachineTemplateModal';
import { useManageMachineTemplates } from '../../hooks/useManageMachineTemplates';
import { useCategories } from '../../../planning/hooks/useCategories';
import { useOptions } from '../../../planning/hooks/useOptions';
import { useSuppliers } from '../../../planning/hooks/useSuppliers';
import { useNotification } from '../../../../context/useNotification';
import { MACHINE_TEMPLATES_TAB_LABELS as LABELS } from '../../../../utils/labels';
import { hasFullAccess } from '../../../../utils/roles';
import type { MemberRole } from '../../../../types/roles';

interface MachineTemplatesTabProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface MachineTemplateModalState {
  mode: 'create' | 'edit';
  template?: SettingsMachineTemplateRow;
}

export default function MachineTemplatesTab({ orgId, isSuperAdmin, role }: MachineTemplatesTabProps) {
  const canEdit = hasFullAccess(role, isSuperAdmin);
  const {
    templates,
    archivedTemplates,
    loadArchived,
    addMachineTemplate,
    updateMachineTemplate,
    archiveMachineTemplate,
    unarchiveMachineTemplate,
    deleteMachineTemplate,
  } = useManageMachineTemplates(orgId);
  const { categories } = useCategories(orgId);
  const { options } = useOptions(orgId);
  const { suppliers } = useSuppliers(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [templateModal, setTemplateModal] = useState<MachineTemplateModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, value: 'active' | 'archived') => {
    setTab(value);
    if (value === 'archived') loadArchived();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteError(null);
    const ok = await deleteMachineTemplate(id);
    if (ok) {
      notifySuccess(LABELS.deleteSuccess);
    } else {
      setDeleteError(LABELS.deleteError);
      notifyError(LABELS.deleteError);
    }
  };

  const handleSave = async (
    name: string,
    categoryId: string | null,
    engineType: Parameters<typeof addMachineTemplate>[2],
    supplierId: string | null,
    optionIds: string[],
    specifications: Parameters<typeof addMachineTemplate>[5],
  ): Promise<boolean> => {
    const ok = templateModal?.mode === 'create'
      ? await addMachineTemplate(name, categoryId, engineType, supplierId, optionIds, specifications)
      : templateModal?.template
        ? await updateMachineTemplate(templateModal.template.id, name, categoryId, engineType, supplierId, optionIds, specifications)
        : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleArchive = async (id: string) => {
    const ok = await archiveMachineTemplate(id);
    if (ok) notifySuccess(LABELS.archiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleUnarchive = async (id: string) => {
    const ok = await unarchiveMachineTemplate(id);
    if (ok) notifySuccess(LABELS.unarchiveSuccess); else notifyError(LABELS.mutationError);
  };

  const list = tab === 'active' ? templates : archivedTemplates;

  return (
    <div className="space-y-6">
      <TabHeader
        title={LABELS.title}
        description={LABELS.subtitle}
        addLabel={canEdit ? LABELS.newButton : undefined}
        onAdd={canEdit ? () => setTemplateModal({ mode: 'create' }) : undefined}
      />

      {deleteError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 flex items-center gap-2">
          <p className="text-sm text-red-700 flex-1">{deleteError}</p>
          <Tooltip title={LABELS.dismissError}>
            <IconButton size="small" onClick={() => setDeleteError(null)} aria-label={LABELS.dismissError}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}

      <div className="rounded-2xl border border-border overflow-hidden bg-surface">
        <Tabs value={tab} onChange={handleTabChange} className="border-b border-border px-2">
          <Tab label={LABELS.tabActive} value="active" />
          <Tab label={LABELS.tabArchived} value="archived" />
        </Tabs>

        {list.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-6">
            {tab === 'active' ? LABELS.noActive : LABELS.noArchived}
          </p>
        ) : (
          list.map((t, i) => (
            <MachineTemplateRow
              key={t.id}
              template={t}
              showDivider={i > 0}
              onEdit={canEdit ? () => setTemplateModal({ mode: 'edit', template: t }) : undefined}
              onArchive={canEdit && !t.archived ? () => handleArchive(t.id) : undefined}
              onUnarchive={canEdit && t.archived ? () => handleUnarchive(t.id) : undefined}
              onDelete={canEdit ? () => setPendingDeleteId(t.id) : undefined}
            />
          ))
        )}
      </div>

      {templateModal && (
        <MachineTemplateModal
          template={templateModal.template}
          categories={categories}
          suppliers={suppliers}
          options={options}
          onClose={() => setTemplateModal(null)}
          onSave={handleSave}
        />
      )}

      <ConfirmDeleteDialog
        open={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
